import type { JSONValue } from "zapatos/db";

import { excludedContent, isExcludedContent } from "./excluded-content";
import {
  parseFragmentEnvelope,
  serializeFragmentEnvelope,
} from "./fragment-envelope";

/**
 * The `content_dynamic_slot_contents.content` jsonb envelope, discriminated by
 * key presence, with exactly one arm per row. Email-owned rows store
 * `{ body: string }`. Message-scoped rows from the SMS and push compiler store
 * `{ fields: { ... } }`. Block-channel rows from the SMS and push composer
 * store `{ version: 1, blocks: [...] }` at the jsonb root. An excluded branch
 * stores `{ excluded: true }` on every channel.
 *
 * Both LCM and CEP write this column, so the reader lives in the neutral base
 * rather than in either product. Per-block validation is deliberately loose,
 * accepting any object with a string `type`. This module discriminates arms,
 * and block vocabularies belong to the readers.
 */
export type SlotBlock = { type: string; [key: string]: JSONValue };
export type SlotContent<TBlock extends SlotBlock = SlotBlock> =
  | { kind: "body"; body: string }
  | { kind: "fields"; fields: Record<string, string> }
  | { kind: "blocks"; blocks: TBlock[] }
  | { kind: "excluded" };

function isSlotBlock(value: unknown): value is SlotBlock {
  return (
    value !== null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    typeof (value as { type?: unknown }).type === "string"
  );
}

export function parseSlotBlocks(value: unknown): SlotBlock[] | null {
  if (!Array.isArray(value)) return null;
  const blocks = value.filter(isSlotBlock);
  if (blocks.length !== value.length) return null;
  return blocks;
}

export function parseSlotContent(content: JSONValue): SlotContent | null {
  if (
    content === null ||
    typeof content !== "object" ||
    Array.isArray(content)
  ) {
    return null;
  }
  const obj = content as Record<string, JSONValue>;
  const hasBody = "body" in obj;
  const hasFields = "fields" in obj;
  const hasBlocks = "blocks" in obj;
  const hasExcluded = "excluded" in obj;
  if (
    Number(hasBody) +
      Number(hasFields) +
      Number(hasBlocks) +
      Number(hasExcluded) !==
    1
  ) {
    return null;
  }

  if (hasExcluded) {
    return isExcludedContent(obj) ? { kind: "excluded" } : null;
  }

  if (hasBody) {
    if (typeof obj.body !== "string") return null;
    return { kind: "body", body: obj.body };
  }

  if (hasFields) {
    const raw = obj.fields;
    if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
      return null;
    }
    const fields: Record<string, string> = {};
    for (const [key, value] of Object.entries(raw)) {
      if (typeof value !== "string") return null;
      fields[key] = value;
    }
    return { kind: "fields", fields };
  }

  const envelope = parseFragmentEnvelope(content, parseSlotBlocks);
  if (envelope === null) return null;
  return { kind: "blocks", blocks: envelope.blocks };
}

export function serializeSlotContent(content: SlotContent): JSONValue {
  switch (content.kind) {
    case "body":
      return { body: content.body };
    case "fields":
      return { fields: content.fields };
    case "blocks":
      return serializeFragmentEnvelope(content.blocks);
    case "excluded":
      return excludedContent();
    default:
      throw new Error(`Unknown content: ${content satisfies never}`);
  }
}
